import { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Upload, Trash2, X, Image as ImageIcon, Plus } from "lucide-react";

const TABS = [
  { k: "property", label: "Property" },
  { k: "images", label: "Images" },
  { k: "contact", label: "Contact & Location" },
  { k: "policies", label: "Policies" },
  { k: "amenities", label: "Amenities" },
  { k: "social", label: "Social Links" },
  { k: "booking-engine", label: "Booking Engine" },
];

const ALL_AMENITIES = [
  "Wifi", "Pool", "Spa", "Gym", "Restaurant", "Bar", "Parking", "Airport Shuttle",
  "Room Service", "24h Front Desk", "Laundry", "AC", "Pet Friendly", "Beach Access",
  "Lake View", "Mountain View", "Garden", "Kids Club", "Business Center", "Concierge",
];

const TIMEZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "Australia/Sydney"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const PROPERTY_TYPES = ["Hotel", "Resort", "Boutique", "B&B", "Homestay", "Villa", "Apartment", "Hostel"];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("property");
  const [hotel, setHotel] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data } = await api.get("/property/details");
    setHotel(data);
    setForm({
      name: data.name || "",
      tagline: data.tagline || "",
      description: data.description || "",
      property_type: data.property_type || "Hotel",
      star_rating: data.star_rating || 4,
      check_in_time: data.check_in_time || "14:00",
      check_out_time: data.check_out_time || "11:00",
      currency: data.currency || "INR",
      timezone: data.timezone || "Asia/Kolkata",
      address_line1: data.address_line1 || "",
      address_line2: data.address_line2 || "",
      city: data.city || "",
      state: data.state || "",
      country: data.country || "India",
      postal_code: data.postal_code || "",
      phone: data.phone || "",
      email: data.email || "",
      website: data.website || "",
      amenities: data.amenities || [],
      policies: {
        cancellation: data.policies?.cancellation || "",
        pet_policy: data.policies?.pet_policy || "",
        smoking_policy: data.policies?.smoking_policy || "",
        extra_bed: data.policies?.extra_bed || "",
        child_policy: data.policies?.child_policy || "",
      },
      social_links: {
        instagram: data.social_links?.instagram || "",
        facebook: data.social_links?.facebook || "",
        twitter: data.social_links?.twitter || "",
        linkedin: data.social_links?.linkedin || "",
        youtube: data.social_links?.youtube || "",
      },
      gst_number: data.gst_number || "",
      brand_color: data.brand_color || "#D4AF37",
      default_language: data.default_language || "English",
      payment_gateway: data.payment_gateway || "Razorpay",
    });
  }
  useEffect(() => { load(); }, []);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function updNested(group, k, v) { setForm((f) => ({ ...f, [group]: { ...f[group], [k]: v } })); }
  function toggleAmenity(a) {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));
  }

  async function save() {
    setSaving(true);
    try {
      await api.patch("/property/details", form);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  if (!hotel || !form) return <div className="text-white/50">Loading…</div>;
  const bookingUrl = user?.hotel_id ? `${window.location.origin}/book/${user.hotel_id}` : "";

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Settings</div>
        <h1 className="font-display text-3xl md:text-4xl">{hotel.name}</h1>
        {hotel.tagline && <div className="text-white/55 mt-2 text-sm italic">{hotel.tagline}</div>}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/5">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`text-sm px-4 py-3 border-b-2 transition ${tab === t.k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/55 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "property" && (
        <Section testid="tab-content-property">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Property name *"><input value={form.name} onChange={(e) => upd("name", e.target.value)} className="input" data-testid="setting-name" /></Field>
            <Field label="Property type"><select value={form.property_type} onChange={(e) => upd("property_type", e.target.value)} className="input">{PROPERTY_TYPES.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Tagline"><input value={form.tagline} onChange={(e) => upd("tagline", e.target.value)} placeholder="A short hook (e.g. Royal heritage on Lake Pichola)" maxLength={160} className="input" data-testid="setting-tagline" /></Field>
            <Field label="Star rating"><select value={form.star_rating} onChange={(e) => upd("star_rating", Number(e.target.value))} className="input">{[3, 4, 5].map((x) => <option key={x} value={x}>{x} Star</option>)}</select></Field>
            <Field label="Check-in time"><input type="time" value={form.check_in_time} onChange={(e) => upd("check_in_time", e.target.value)} className="input" /></Field>
            <Field label="Check-out time"><input type="time" value={form.check_out_time} onChange={(e) => upd("check_out_time", e.target.value)} className="input" /></Field>
            <Field label="Currency"><select value={form.currency} onChange={(e) => upd("currency", e.target.value)} className="input">{CURRENCIES.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Timezone"><select value={form.timezone} onChange={(e) => upd("timezone", e.target.value)} className="input">{TIMEZONES.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="GST / Tax number"><input value={form.gst_number} onChange={(e) => upd("gst_number", e.target.value)} className="input" data-testid="setting-gst" /></Field>
            <Field label="Brand color"><input type="color" value={form.brand_color} onChange={(e) => upd("brand_color", e.target.value)} className="input h-11 w-24 p-1" /></Field>
          </div>
          <Field label="Description"><textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={5} maxLength={4000} placeholder="Tell guests what makes this property special — heritage, design, location, signature experiences." className="input" data-testid="setting-description" /></Field>
          <SaveBtn onClick={save} saving={saving} />
        </Section>
      )}

      {tab === "images" && (
        <ImagesPanel hotel={hotel} reload={load} />
      )}

      {tab === "contact" && (
        <Section testid="tab-content-contact">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Phone"><input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+91 98765 43210" className="input" data-testid="setting-phone" /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="hello@property.com" className="input" data-testid="setting-email" /></Field>
            <Field label="Website" className="md:col-span-2"><input value={form.website} onChange={(e) => upd("website", e.target.value)} placeholder="https://www.example.com" className="input" data-testid="setting-website" /></Field>
            <Field label="Address line 1" className="md:col-span-2"><input value={form.address_line1} onChange={(e) => upd("address_line1", e.target.value)} className="input" data-testid="setting-addr1" /></Field>
            <Field label="Address line 2" className="md:col-span-2"><input value={form.address_line2} onChange={(e) => upd("address_line2", e.target.value)} className="input" /></Field>
            <Field label="City"><input value={form.city} onChange={(e) => upd("city", e.target.value)} className="input" data-testid="setting-city" /></Field>
            <Field label="State"><input value={form.state} onChange={(e) => upd("state", e.target.value)} className="input" /></Field>
            <Field label="Country"><input value={form.country} onChange={(e) => upd("country", e.target.value)} className="input" /></Field>
            <Field label="Postal code"><input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} className="input" /></Field>
          </div>
          <SaveBtn onClick={save} saving={saving} />
        </Section>
      )}

      {tab === "policies" && (
        <Section testid="tab-content-policies">
          <Field label="Cancellation policy"><textarea value={form.policies.cancellation} onChange={(e) => updNested("policies", "cancellation", e.target.value)} rows={3} placeholder="e.g. Free cancellation up to 24 hours before check-in." className="input" data-testid="policy-cancellation" /></Field>
          <Field label="Pet policy"><textarea value={form.policies.pet_policy} onChange={(e) => updNested("policies", "pet_policy", e.target.value)} rows={2} className="input" data-testid="policy-pet" /></Field>
          <Field label="Smoking policy"><textarea value={form.policies.smoking_policy} onChange={(e) => updNested("policies", "smoking_policy", e.target.value)} rows={2} className="input" /></Field>
          <Field label="Extra bed"><textarea value={form.policies.extra_bed} onChange={(e) => updNested("policies", "extra_bed", e.target.value)} rows={2} className="input" /></Field>
          <Field label="Child policy"><textarea value={form.policies.child_policy} onChange={(e) => updNested("policies", "child_policy", e.target.value)} rows={2} className="input" /></Field>
          <SaveBtn onClick={save} saving={saving} />
        </Section>
      )}

      {tab === "amenities" && (
        <Section testid="tab-content-amenities">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Select all that apply</div>
          <div className="flex flex-wrap gap-2">
            {ALL_AMENITIES.map((a) => {
              const on = form.amenities.includes(a);
              return (
                <button key={a} onClick={() => toggleAmenity(a)} type="button" data-testid={`amenity-${a.replace(/\s+/g, "-")}`}
                  className={`text-xs px-3 py-2 rounded-md border transition ${on ? "bg-[#D4AF37]/15 border-[#D4AF37]/50 text-[#D4AF37]" : "bg-[#0a0f1c] border-white/10 text-white/60 hover:border-white/30"}`}>
                  {a}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-white/40 mt-3">{form.amenities.length} selected</div>
          <SaveBtn onClick={save} saving={saving} />
        </Section>
      )}

      {tab === "social" && (
        <Section testid="tab-content-social">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Instagram"><input value={form.social_links.instagram} onChange={(e) => updNested("social_links", "instagram", e.target.value)} placeholder="https://instagram.com/yourbrand" className="input" data-testid="social-instagram" /></Field>
            <Field label="Facebook"><input value={form.social_links.facebook} onChange={(e) => updNested("social_links", "facebook", e.target.value)} placeholder="https://facebook.com/yourbrand" className="input" /></Field>
            <Field label="X / Twitter"><input value={form.social_links.twitter} onChange={(e) => updNested("social_links", "twitter", e.target.value)} placeholder="https://x.com/yourbrand" className="input" /></Field>
            <Field label="LinkedIn"><input value={form.social_links.linkedin} onChange={(e) => updNested("social_links", "linkedin", e.target.value)} className="input" /></Field>
            <Field label="YouTube" className="md:col-span-2"><input value={form.social_links.youtube} onChange={(e) => updNested("social_links", "youtube", e.target.value)} className="input" /></Field>
          </div>
          <SaveBtn onClick={save} saving={saving} />
        </Section>
      )}

      {tab === "booking-engine" && (
        <Section testid="tab-content-booking-engine">
          <Field label="Direct booking URL">
            <div className="flex gap-2">
              <input value={bookingUrl} readOnly className="input font-mono text-xs" />
              <button onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success("Copied"); }} className="btn-ghost-gold rounded-md px-4 text-sm">Copy</button>
              <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-ghost-gold rounded-md px-4 text-sm flex items-center">Open</a>
            </div>
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Default language"><select value={form.default_language} onChange={(e) => upd("default_language", e.target.value)} className="input">{["English", "Hindi", "Arabic", "French", "Spanish"].map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Payment gateway"><select value={form.payment_gateway} onChange={(e) => upd("payment_gateway", e.target.value)} className="input">{["Razorpay", "PayU", "Stripe"].map((x) => <option key={x}>{x}</option>)}</select></Field>
          </div>
          <SaveBtn onClick={save} saving={saving} />
        </Section>
      )}

      <style>{`.input { width: 100%; background: #0a0f1c; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px 12px; color: #F8F9FA; font-size: 14px; outline: none; } .input:focus { border-color: rgba(212, 175, 55, 0.6); }`}</style>
    </div>
  );
}

function Section({ children, testid }) {
  return <div className="bg-[#12182B] border border-white/5 rounded-xl p-6 space-y-5" data-testid={testid}>{children}</div>;
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      {children}
    </div>
  );
}

function SaveBtn({ onClick, saving }) {
  return (
    <button onClick={onClick} disabled={saving} className="btn-gold rounded-md px-5 py-3 text-sm disabled:opacity-60" data-testid="save-settings">
      {saving ? "Saving…" : "Save changes"}
    </button>
  );
}

// ============ Images Panel ============

const MAX_BYTES = 2 * 1024 * 1024;

function ImagesPanel({ hotel, reload }) {
  return (
    <Section testid="tab-content-images">
      <div className="grid md:grid-cols-2 gap-6">
        <SingleImageSlot label="Logo" kind="logo" current={hotel.logo} reload={reload} />
        <SingleImageSlot label="Hero image" kind="hero" current={hotel.hero} reload={reload} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-white/50 mb-3 flex items-center justify-between">
          <span>Gallery ({(hotel.gallery || []).length}/8)</span>
          <AddGalleryButton disabled={(hotel.gallery || []).length >= 8} reload={reload} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {(hotel.gallery || []).map((g) => (
            <GalleryItem key={g.id} image={g} reload={reload} />
          ))}
          {(hotel.gallery || []).length === 0 && (
            <div className="col-span-full text-center py-10 text-white/40 text-sm border border-dashed border-white/10 rounded-md">
              No gallery images yet. Add up to 8 — they appear on your booking page.
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function SingleImageSlot({ label, kind, current, reload }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("Image exceeds 2 MB"); return; }
    const data_url = await fileToDataUrl(file);
    setUploading(true);
    try {
      await api.post("/property/images", { kind, data_url });
      toast.success(`${label} uploaded`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function remove() {
    try {
      await api.delete(`/property/images/${kind}`);
      toast.success(`${label} removed`); reload();
    } catch (e) { toast.error("Delete failed"); }
  }

  return (
    <div data-testid={`image-slot-${kind}`}>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      <div className="relative aspect-video rounded-md border border-white/10 bg-[#0a0f1c] overflow-hidden">
        {current?.data_url ? (
          <img src={current.data_url} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/30">
            <ImageIcon className="w-8 h-8 mb-2" />
            <span className="text-xs">No {label.toLowerCase()}</span>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`upload-${kind}-btn`}
          className="rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 border border-white/10 text-white/80 hover:bg-white/5">
          <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload"}
        </button>
        {current?.data_url && (
          <button type="button" onClick={remove} data-testid={`remove-${kind}-btn`}
            className="rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 border border-white/10 text-white/60 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-300">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" />
      </div>
      <div className="text-[11px] text-white/40 mt-2">PNG/JPG/WEBP · max 2 MB</div>
    </div>
  );
}

function AddGalleryButton({ disabled, reload }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("Image exceeds 2 MB"); return; }
    const data_url = await fileToDataUrl(file);
    setUploading(true);
    try {
      await api.post("/property/images", { kind: "gallery", data_url });
      toast.success("Added to gallery"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  }

  return (
    <>
      <button onClick={() => inputRef.current?.click()} disabled={disabled || uploading} data-testid="add-gallery-btn"
        className="rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-40 disabled:cursor-not-allowed">
        <Plus className="w-3 h-3" /> {uploading ? "Uploading…" : "Add image"}
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" />
    </>
  );
}

function GalleryItem({ image, reload }) {
  const [removing, setRemoving] = useState(false);
  async function remove() {
    setRemoving(true);
    try {
      await api.delete(`/property/images/gallery?image_id=${image.id}`);
      reload();
    } catch (e) { toast.error("Delete failed"); }
    finally { setRemoving(false); }
  }
  return (
    <div className="relative aspect-square rounded-md overflow-hidden border border-white/10 group" data-testid={`gallery-item-${image.id}`}>
      <img src={image.data_url} alt={image.caption || ""} className="w-full h-full object-cover" />
      <button onClick={remove} disabled={removing} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 backdrop-blur opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white/80 hover:bg-red-500/80 hover:text-white" data-testid={`gallery-remove-${image.id}`}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
